import React, { useMemo } from "react";
import ReactApexChart from "react-apexcharts";
import { useSelector } from "react-redux";
import LabelField from "../Form/FormFields/LabelField";
import DynamicLoader from "../Form/FormFields/DynamicLoader";
import Accordion from "../ui/Accordion";
import { RootState } from "../../utils/store";
import { chartConfigGenerator } from "./ChartConfigGenerator";
import NoteComponent from "../ui/table/Notes";

const DynamicStackedColumnChart: React.FC<any> = ({ chartListData, width }) => {
  const selectorData = useSelector((state: RootState) => state.dynamicData);
  const notes = chartListData?.note;
  const noteData = chartListData?.remarks;
  const chartData = selectorData?.[chartListData?.id];
  const isLoading =
    selectorData?.loaders?.[`report/chart/${chartListData?.id}`];

  const isChartReady =
    chartData &&
    Array.isArray(chartData?.label_name) &&
    chartData?.label_name?.length > 0 &&
    Array.isArray(chartData?.data) &&
    chartData?.data?.length > 0;

  const { series, categories, chartType, colors } = useMemo(() => {
    if (!isChartReady)
      return { series: [], categories: [], chartType: "bar", colors: [] };
    return chartConfigGenerator(chartData.data, chartData.label_name);
  }, [isChartReady, chartData]);

  const options: any = {
    chart: {
      type: chartType,
      stacked: true,
      toolbar: { show: false },
    },
    plotOptions: {
      bar: { horizontal: false, columnWidth: "60%" },
    },
    dataLabels: { enabled: false },
    xaxis: {
      categories,
      title: { text: chartData?.label_name?.[0]?.x?.title || "X Axis" },
    },
    yaxis: {
      title: {
        text: chartData?.label_name?.[0]?.y1?.title || "Applications Count",
      },
      labels: {
        formatter: (val: number) => val.toLocaleString(),
      },
    },
    legend: {
      position: "top",
      horizontalAlign: "center",
    },
    tooltip: {
      shared: true,
      intersect: false,
      y: {
        formatter: (val: number, opts: any) => {
          const seriesName = opts.w.globals.seriesNames[opts.seriesIndex];
          return `${seriesName}: ${val.toLocaleString()}`;
        },
      },
      x: {
        formatter: (val: string) => `${val}`,
      },
    },
    // colors: [
    //   "#60a5fa",
    //   "#a5b4fc",
    //   "#f97316",
    //   "#ef4444",
    //   "#10b981",
    //   "#6366f1",
    //   "#f59e0b",
    //   "#3b82f6",
    //   "#8b5cf6",
    //   "#ec4899",
    //   "#14b8a6",
    //   "#eab308",
    // ],

    colors,
  };

  return (
    <div className={`grid gap-2 ${width && width}`}>
      <div className="bg-white dark:bg-slate-800 p-4 rounded-lg shadow-md w-full">
        <h2 className="text-lg font-semibold mb-2">
          <div className="flex justify-between items-center mb-1">
            <div className="text-[#464E5F] text-[14px] font-semibold flex items-center">
              <LabelField label={chartListData?.title} />
              {notes && (
                <NoteComponent
                  notes={noteData}
                  modalTitle={chartListData?.title}
                />
              )}
            </div>
          </div>
        </h2>

        {isLoading ? (
          <DynamicLoader loading={isLoading} />
        ) : isChartReady ? (
          <>
            <div className="w-full items-center mb-3" style={{ height: 400 }}>
              <ReactApexChart
                options={options}
                series={series}
                type="bar"
                height="100%"
              />
            </div>
            <Accordion
              title="AI Insights"
              componentID={chartListData?.id}
              chartData={chartListData}
            />
          </>
        ) : (
          <div className="text-center text-gray-500 py-10">
            No chart data available
          </div>
        )}
      </div>
    </div>
  );
};

export default DynamicStackedColumnChart;
